import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import {
  Volume2,
  Users,
  Languages,
  Bot,
  BookPlus,
  ArrowRight,
  Loader2,
  Mail,
  Bell,
} from "lucide-react";
import { toast } from "sonner";
import { addToDictionary, detectLanguage, generateAcronym } from "@/lib/aacDictionary";
import { supabase } from "@/integrations/supabase/client";
import { speak } from "@/lib/ttsService";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "@/hooks/useTranslation";

type View = "main" | "translate" | "sendChoice";

interface ContactConfig {
  hasEmail: boolean;
  hasPush: boolean;
  email?: string;
}

const targetLanguages = [
  { code: "en", name: "English", nameHe: "אנגלית" },
  { code: "he", name: "עברית", nameHe: "עברית" },
  { code: "ru", name: "Русский", nameHe: "רוסית" },
  { code: "ar", name: "العربية", nameHe: "ערבית" },
  { code: "es", name: "Español", nameHe: "ספרדית" },
];

export default function KeyboardActions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, lang, dir } = useTranslation();
  
  // Get text from navigation state
  const text = location.state?.text || "";
  const returnPath = location.state?.returnPath || "/keyboard";
  
  const [view, setView] = useState<View>("main");
  const [translatedText, setTranslatedText] = useState("");
  const [targetLang, setTargetLang] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [contactConfig, setContactConfig] = useState<ContactConfig>({ hasEmail: false, hasPush: false });
  const [isSendingPush, setIsSendingPush] = useState(false);

  const isHebrew = lang === "he";
  const isTextHebrew = detectLanguage(text) === "he";
  
  // Fetch contact configuration
  useEffect(() => {
    const fetchContactConfig = async () => {
      const caregiverData = localStorage.getItem("ayuni-caregiver");
      const caregiver = caregiverData ? JSON.parse(caregiverData) : null;
      const hasEmail = !!caregiver?.email;
      
      let hasPush = false;
      if (user) {
        const { data } = await supabase
          .from('user_settings')
          .select('caregiver_push_tokens')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (data?.caregiver_push_tokens && Array.isArray(data.caregiver_push_tokens)) {
          hasPush = data.caregiver_push_tokens.length > 0;
        }
      }
      
      setContactConfig({ hasEmail, hasPush, email: caregiver?.email });
    };
    
    fetchContactConfig();
  }, [user]);
  
  const handleClose = () => {
    // Navigate back and clear the text
    navigate(returnPath, { state: { clearText: true } });
  };

  const handleSendEmail = (messageText: string = text) => {
    const email = contactConfig.email;
    if (!email) return;
    
    const subject = encodeURIComponent(isHebrew ? "הודעה מ-EyeControl App" : "Message from EyeControl App");
    const body = encodeURIComponent(messageText);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    handleClose();
  };

  const handleSendPush = async (messageText: string = text) => {
    if (!user) return;
    
    setIsSendingPush(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-push-alert', {
        body: {
          patientId: user.id,
          alertType: 'help',
          message: messageText,
        }
      });
      
      if (error) throw error;
      
      if (data?.sent > 0) {
        toast.success(isHebrew ? `נשלחה התראה ל-${data.sent} מכשירים` : `Sent to ${data.sent} devices`);
      } else {
        toast.info(isHebrew ? "אין מטפלים מחוברים" : "No caregivers connected");
      }
      
      handleClose();
    } catch (error) {
      toast.error(isHebrew ? "שגיאה בשליחה" : "Failed to send");
    } finally {
      setIsSendingPush(false);
    }
  };

  const handleSendToContacts = () => {
    const canEmail = contactConfig.hasEmail;
    const canPush = contactConfig.hasPush;
    
    if (canEmail && canPush) {
      setView("sendChoice");
    } else if (canEmail) {
      handleSendEmail();
    } else if (canPush) {
      handleSendPush();
    } else {
      sessionStorage.setItem("sendMessageText", text);
      navigate("/contacts?action=send");
    }
  };

  const handleSpeak = async (textToSpeak: string = text, langCode?: string) => {
    if (!textToSpeak.trim()) return;
    
    const language = langCode || (detectLanguage(textToSpeak) === "he" ? "he" : "en");
    
    toast.success(isHebrew ? "מדבר..." : "Speaking...");
    
    await speak({
      text: textToSpeak,
      language,
      onError: () => {
        toast.error(isHebrew ? "שגיאה בהקראה" : "Speech error");
      },
    });
  };

  const handleTranslate = async (langCode: string) => {
    setTargetLang(langCode);
    setIsTranslating(true);
    
    try {
      const langNames: Record<string, string> = {
        en: "English",
        he: "Hebrew",
        ru: "Russian",
        ar: "Arabic",
        es: "Spanish",
      };
      
      const { data, error } = await supabase.functions.invoke('translate', {
        body: { 
          text,
          targetLanguage: langNames[langCode] || langCode
        }
      });
      
      if (error) throw error;
      
      setTranslatedText(data.translatedText || text);
      toast.success(isHebrew ? "תורגם!" : "Translated!");
    } catch (err) {
      setTranslatedText(text);
      toast.error(isHebrew ? "שגיאה בתרגום" : "Translation error");
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSendToAIChat = (textToSend: string = text) => {
    sessionStorage.setItem("aiChatInitialMessage", textToSend);
    navigate("/ai-chat");
  };

  const handleAddToDictionary = () => {
    if (!text.trim()) return;
    const entry = addToDictionary(text);
    toast.success(
      isHebrew ? "נוסף למילון!" : "Added to dictionary!",
      { 
        description: isHebrew 
          ? `ראשי תיבות: ${entry.acronym}` 
          : `Acronym: ${entry.acronym}` 
      }
    );
    handleClose();
  };

  const resetView = () => {
    setView("main");
    setTranslatedText("");
    setTargetLang("");
  };

  // If no text, redirect back
  useEffect(() => {
    if (!text) {
      navigate(returnPath);
    }
  }, [text, navigate, returnPath]);

  if (!text) return null;

  return (
    <>
      <Helmet>
        <title>{isHebrew ? "פעולות" : "Actions"} - EyeControl App</title>
      </Helmet>

      <MainLayout>
        <div className="flex-1 flex flex-col bg-background p-6" dir={dir}>
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="lg"
              onClick={handleClose}
              className="gap-2"
              data-scan-item
            >
              <ArrowRight className={`w-5 h-5 ${!isHebrew ? "rotate-180" : ""}`} />
              {isHebrew ? "חזרה" : "Back"}
            </Button>
            <h1 className="text-2xl font-bold text-foreground">
              {view === "main" 
                ? (isHebrew ? "פעולות" : "Actions")
                : view === "sendChoice"
                ? (isHebrew ? "שליחה למטפל" : "Send to Caregiver")
                : (isHebrew ? "תרגום" : "Translate")
              }
            </h1>
          </div>

          <div className="max-w-2xl mx-auto w-full space-y-6">
            {view === "main" && (
              <>
                {/* Preview text */}
                <div className="p-4 bg-muted rounded-xl border border-border">
                  <p className="text-foreground font-medium text-lg">{text}</p>
                </div>

                {/* Action buttons */}
                <div className="grid grid-cols-1 gap-3">
                  <Button
                    variant="outline"
                    className="justify-start gap-4 h-16 text-lg"
                    onClick={() => handleSpeak()}
                    data-scan-item
                  >
                    <Volume2 className="w-6 h-6 text-primary" />
                    {isHebrew ? "השמעה ברמקול" : "Speak"}
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start gap-4 h-16 text-lg"
                    onClick={handleSendToContacts}
                    data-scan-item
                  >
                    <Users className="w-6 h-6 text-amber-500" />
                    {isHebrew ? "שליחה דרך צור קשר" : "Send via Contacts"}
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start gap-4 h-16 text-lg"
                    onClick={() => handleSendToAIChat()}
                    data-scan-item
                  >
                    <Bot className="w-6 h-6 text-green-500" />
                    {isHebrew ? "שליחה לצ'אט AI" : "Send to AI Chat"}
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start gap-4 h-16 text-lg"
                    onClick={() => setView("translate")}
                    data-scan-item
                  >
                    <Languages className="w-6 h-6 text-blue-500" />
                    {isHebrew ? "תרגום" : "Translate"}
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start gap-4 h-16 text-lg"
                    onClick={handleAddToDictionary}
                    data-scan-item
                  >
                    <BookPlus className="w-6 h-6 text-purple-500" />
                    {isHebrew ? "הוספה להשלמת מילים" : "Add to Dictionary"}
                    <span className="text-sm text-muted-foreground mr-auto">
                      ({generateAcronym(text)})
                    </span>
                  </Button>
                </div>
              </>
            )}

            {view === "sendChoice" && (
              <>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => setView("main")}
                  className="gap-2"
                  data-scan-item
                >
                  <ArrowRight className={`w-5 h-5 ${!isHebrew ? "rotate-180" : ""}`} />
                  {isHebrew ? "חזור" : "Back"}
                </Button>

                <div className="p-4 bg-muted rounded-xl border border-border">
                  <p className="text-foreground font-medium text-lg">{text}</p>
                </div>

                <p className="text-muted-foreground">
                  {isHebrew ? "בחר אופן שליחה:" : "Choose send method:"}
                </p>

                <div className="grid grid-cols-1 gap-3">
                  <Button
                    variant="outline"
                    className="justify-start gap-4 h-16 text-lg"
                    onClick={() => handleSendEmail()}
                    disabled={!contactConfig.hasEmail}
                    data-scan-item
                  >
                    <Mail className="w-6 h-6 text-primary" />
                    {isHebrew ? "אימייל" : "Email"}
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start gap-4 h-16 text-lg"
                    onClick={() => handleSendPush()}
                    disabled={!contactConfig.hasPush || isSendingPush}
                    data-scan-item
                  >
                    {isSendingPush ? (
                      <Loader2 className="w-6 h-6 animate-spin text-accent" />
                    ) : (
                      <Bell className="w-6 h-6 text-accent" />
                    )}
                    {isHebrew ? "התראת Push" : "Push Alert"}
                  </Button>
                </div>
              </>
            )}

            {view === "translate" && (
              <>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={resetView}
                  className="gap-2"
                  data-scan-item
                >
                  <ArrowRight className={`w-5 h-5 ${!isHebrew ? "rotate-180" : ""}`} />
                  {isHebrew ? "חזור" : "Back"}
                </Button>

                {!translatedText && !isTranslating ? (
                  <>
                    <p className="text-muted-foreground">
                      {isHebrew ? "בחר שפת יעד:" : "Select target language:"}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {targetLanguages.map((langItem) => (
                        <Button
                          key={langItem.code}
                          variant="outline"
                          onClick={() => handleTranslate(langItem.code)}
                          className="h-14 text-lg"
                          data-scan-item
                        >
                          {isHebrew ? langItem.nameHe : langItem.name}
                        </Button>
                      ))}
                    </div>
                  </>
                ) : isTranslating ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <span className="mr-3 text-muted-foreground text-lg">
                      {isHebrew ? "מתרגם..." : "Translating..."}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="p-4 bg-muted rounded-xl border border-border">
                      <p className="text-xs text-muted-foreground mb-1">
                        {isHebrew ? "מקור:" : "Original:"}
                      </p>
                      <p className="text-foreground">{text}</p>
                    </div>

                    <div className="p-4 bg-primary/10 rounded-xl border border-primary/20">
                      <p className="text-xs text-muted-foreground mb-1">
                        {isHebrew ? "תרגום:" : "Translation:"}
                      </p>
                      <p className="text-foreground font-medium text-lg">{translatedText}</p>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <Button
                        variant="outline"
                        className="justify-start gap-4 h-14"
                        onClick={() => handleSpeak(translatedText, targetLang)}
                        data-scan-item
                      >
                        <Volume2 className="w-5 h-5 text-primary" />
                        {isHebrew ? "השמע תרגום" : "Speak Translation"}
                      </Button>

                      <Button
                        variant="outline"
                        className="justify-start gap-4 h-14"
                        onClick={() => {
                          sessionStorage.setItem("sendMessageText", translatedText);
                          navigate("/contacts?action=send");
                        }}
                        data-scan-item
                      >
                        <Users className="w-5 h-5 text-amber-500" />
                        {isHebrew ? "שלח לאנשי קשר" : "Send to Contacts"}
                      </Button>

                      <Button
                        variant="outline"
                        className="justify-start gap-4 h-14"
                        onClick={() => handleSendToAIChat(translatedText)}
                        data-scan-item
                      >
                        <Bot className="w-5 h-5 text-green-500" />
                        {isHebrew ? "שלח לצ'אט AI" : "Send to AI Chat"}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </MainLayout>
    </>
  );
}
